// PartialOrder.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Select from "react-select";
import { Navigate } from "react-router-dom";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import Export from "../../../../formHelper/export";
import Search from "../../../../formHelper/search";
import Pagination from "../../../../formHelper/pagination";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Success from "../../../../formHelper/success";
import Delete from "../../../../formHelper/delete";
import exportApiCall from "../../../../apiHelper/export";
import FilterDropdown from "../../../../formHelper/filterDropDown";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { parseValidationErrors } from "../../../../apiHelper/validationHelper";
import PartialOrderTable from "../../../../formHelper/PartialOrderTable";
import Upload from "../invoice/upload";
import apiFileRequestCall from "../../../../apiHelper/filleHelper";
import Loader from "../../../../formHelper/Loader";
import QuillEditor from "../../../../formHelper/QuillEditor";
import type { RootState, AppDispatch } from "../../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";

type Id = number;

interface Branch {
  id: Id;
  name: string;
}
interface CustomerDD {
  id: Id;
  company_name: string;
  owner_name: string;
  address: string;
  state_id: Id;
  other_state?: any;
  city?: any;
  mobile_no?: any;
  email_id?: any;
  landline_no?: any;
  pin_code?: any;
  customer_name?: any;
  owner?: { id: Id; name: string } | null;
}
interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

type FormError = {
  product_list?: { [index: number]: { [field: string]: string | undefined } };
} & { [key: string]: string | undefined };

interface ProductRow {
  [k: string]: any;
}

type ErrorBag = Record<string, any>;

const DEFAULT_PAYMENT_TERMS = `<ol style="padding-left: 20px;">
  <li style="margin-bottom: 8px;">Quotation is Valid for 30 days.</li>
  <li style="margin-bottom: 8px;">Payment Terms: Advance Against Proforma Invoice.</li>
  <li style="margin-bottom: 8px;">
    Any Government / Local Body Levies, taxes, Cess, Duties, Octroi 
    will be extra <strong>"At Actuals"</strong>.
  </li>
  <li style="margin-bottom: 8px;">
    The Delivery Schedule mentioned against each item is as per the 
    Quotation Date and is subject to prior sales.
  </li>
  <li style="margin-bottom: 8px;">
    <strong>Loading/Unloading:</strong> Material unloading on site will be in your scope.
  </li>
</ol>`;

/* ---------- Helpers ---------- */
const makeOptions = (
  list: Branch[] | CustomerDD[],
  labelKey = "name",
  extra: (i: any) => any = () => ({}),
) =>
  (list as any[]).map((b: any) => ({
    value: b.id,
    label: b[labelKey],
    ...extra(b),
  }));

const numberOrZero = (v: any) => {
  const n = parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
};

type EditorState = {
  index: number;
  field: "notes" | "description" | "product_specification" | "Comments";
} | null;

const PartialOrder: React.FC = () => {
  const backendRoute = all_routes.backend;

  /* ---------- State ---------- */
  const [list, setList] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    product_specification: DEFAULT_PAYMENT_TERMS,
    payment_term_condition: DEFAULT_PAYMENT_TERMS,
    date: dayjs().format("YYYY-MM-DD"),
    delivery_date_id: "",
    quotation_type_id: null,
    group_id: null,
    company_id: null,
    owner_id: null,
    principal_id: null,
    billing_state_id: null,
    shipping_state_id: null,
    product_description: "",
    enq_ref: "",
    contact_person: "",
    prepard_by: "",
    currency_id: null,
    delevery_date_id: null,
    payment_term_id: null,
    product_id: null,
    principal_type: "",
    billing_address: "",
    billing_state: "",
    billing_city: "",
    billing_pin_code: "",
    billing_mobile: "",
    billing_email: "",
    billing_landline: "",
    shipping_address: "",
    shipping_state: "",
    shipping_city: "",
    shipping_pin_code: "",
    shipping_mobile: "",
    shipping_email: "",
    shipping_landline: "",
    total_amount: 0,
    customer_order_no: "",
    unique_order_no: "",
  });
  const [formError, setFormError] = useState<FormError>({});
  const [invoiceFormError, setInvoiceFormError] = useState<ErrorBag>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editor, setEditor] = useState<EditorState>(null);
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [quotationType, setQuotationType] = useState("CW");
  const [closeOrderTitle, setCloseOrderTitle] = useState(false);
  const [editorValue, setEditorValue] = useState("");
  const openEditor = (index: number, field: any) =>
    setEditor({
      index,
      field: field === "Comments" ? "product_specification" : field,
    });
  const closeEditor = () => setEditor(null);
  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      return 5;
    }
    return 10;
  });
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  /* filters (no status) */
  const [ownerFilter, setOwnerFilter] = useState<number[]>([]);
  const [branchFilter, setBranchFilter] = useState<number[]>([]);
  const [principalFilter, setPrincipalFilter] = useState<number[]>([]);
  const [currencyFilter, setCurrencyFilter] = useState<number[]>([]);

  /* dropdowns */
  const [companyList, setCompanyList] = useState<CustomerDD[]>([]);
  const [ownerList, setOwnerList] = useState<Branch[]>([]);
  const [quotationTypeList, setQuotationTypeList] = useState<Branch[]>([]);
  const [notificationList, setNotificationList] = useState<Branch[]>([]);
  const [currencyList, setCurrencyList] = useState<Branch[]>([]);
  const [branchList, setBranchList] = useState<Branch[]>([]);
  const [stateList, setStateList] = useState<Branch[]>([]);
  const [paymentAdvanceDate, setPaymentAdvanceDate] = useState<Branch[]>([]);
  const [productRows, setProductRows] = useState<ProductRow[]>([]);
  const [orderTitle, setOrderTitle] = useState(false);
  const [courierList, setCourierList] = useState<Branch[]>([]);
  const [currencyName, setCurrencyName] = useState("INR");
  const [submitting, setSubmitting] = useState(false);
  const [addressInfo, setAddressInfo] = useState<any>({});
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [showUpload, setShowUpload] = useState(false);
  const [blinkField, setBlinkField] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  /* export/polling states */
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  /* invoice upload state */
  interface ProductInvoiceRow {
    invoice: File | null;
  }
  interface InvoiceFormData {
    current_follow_date: string;
    invoice_no: string;
    docket_no: string;
    partial_order_id: any;
    customer_id: any;
    customer_order_no: any;
  }
  const [invoiceDate, setInvoiceDate] = useState<InvoiceFormData>({
    current_follow_date: dayjs().format("YYYY-MM-DD"),
    invoice_no: "",
    customer_id: "",
    docket_no: "",
    partial_order_id: null,
    customer_order_no: "",
  });
  const [productInvoiceRows, setProductInvoiceRows] = useState<
    ProductInvoiceRow[]
  >([{ invoice: null }]);

  const styleTagRef = useRef<HTMLStyleElement | null>(null);

  /* ---------- Small helpers ---------- */
  const setField = useCallback((name: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    setFormError((prev) => {
      if (!prev) return prev;
      const u = { ...prev };
      delete (u as any)[name];
      return u;
    });
  }, []);

  /* ---------- Options memo ---------- */
  const companyOptions = useMemo(
    () =>
      makeOptions(companyList, "company_name", (b: CustomerDD) => ({
        address: b.address,
        owner_name: b.owner_name,
        state_id: b.state_id,
        other_state: b.other_state,
        city: b.city,
        mobile_no: b.mobile_no,
        email_id: b.email_id,
        landline_no: b.landline_no,
        pin_code: b.pin_code,
        customer_name: b.customer_name,
      })),
    [companyList],
  );
  const ownerOptions = useMemo(() => makeOptions(ownerList), [ownerList]);
  const quotationTypeOptions = useMemo(
    () => makeOptions(quotationTypeList),
    [quotationTypeList],
  );
  const notificationOptions = useMemo(
    () => makeOptions(notificationList),
    [notificationList],
  );
  const stateOptions = useMemo(() => makeOptions(stateList), [stateList]);
  const currencyOptions = useMemo(
    () => makeOptions(currencyList),
    [currencyList],
  );
  const paymentAdvanceOptions = useMemo(
    () => makeOptions(paymentAdvanceDate),
    [paymentAdvanceDate],
  );
  const courierOptions = useMemo(() => makeOptions(courierList), [courierList]);

  /* ---------- Totals ---------- */
  const subTotal = useMemo(
    () => productRows.reduce((sum, r) => sum + numberOrZero(r.total), 0),
    [productRows],
  );

  useEffect(() => {
    setFormData((prev: any) =>
      prev.total_amount === subTotal
        ? prev
        : { ...prev, total_amount: subTotal },
    );
  }, [subTotal]);

  /* ---------- Fetch list ---------- */
  const fetchList = useCallback(
    async ({ page = 1, perPage = 10, search = "" }) => {
      setLoading(true);
      if (search) {
        setLoadingSearch(true);
      }
      try {
        const result = await apiRequestCall(
          "post",
          backendRoute.getPartialOrder,
          {
            data: {
              page,
              per_page: perPage,
              start_date: startDate || undefined,
              end_date: endDate || undefined,
              search: searchText,
              owner_list: ownerFilter,
              branch_list: branchFilter,
              principal_list: principalFilter,
              currency_list: currencyFilter,
            },
          },
        );
        const res = result?.data?.data;
        if (res) {
          // flatten order_details -> rows (same as your existing logic)
          const flattenedList = (res.data || []).flatMap(
            (partialOrder: any) => {
              const companyName =
                partialOrder.company_details?.company_name || "";
              const branchName = partialOrder.branch_details?.name || "";
              const partialOrderId = partialOrder.id ?? null;
              let company_id_info = partialOrder?.company_id;
              return (partialOrder.order_details || []).map((item: any) => ({
                ...item,
                company_name: companyName,
                branch_name: branchName,
                customer_order_no: partialOrder.customer_order_no,
                total_amount: item?.total,
                rate: item?.net_price,
                created_at: partialOrder.created_at,
                balance_quantity: item.balance_quantity,
                ship_quantity: item.send_qty,
                billing_address: partialOrder?.billing_address,
                billing_city: partialOrder?.billing_city,
                billing_contact_person: partialOrder?.billing_contact_person,
                billing_email: partialOrder?.billing_email,
                billing_landline: partialOrder?.billing_landline,
                billing_mobile: partialOrder?.billing_mobile,
                billing_pin_code: partialOrder?.billing_pin_code,
                billing_state_id: partialOrder?.billing_state_id,
                shipping_address: partialOrder?.shipping_address,
                shipping_city: partialOrder?.shipping_city,
                shipping_email: partialOrder?.shipping_email,
                shipping_landline: partialOrder?.shipping_landline,
                shipping_mobile: partialOrder?.shipping_mobile,
                shipping_pin_code: partialOrder?.shipping_pin_code,
                shipping_state_id: partialOrder?.shipping_state_id,
                product_list: (partialOrder.order_details || []).map(
                  (p: any) => ({
                    ...p,
                    is_checked: 1,
                  }),
                ),
                is_checked: item?.is_checked,
                invoice_id: partialOrder?.invoice_id,
                company_id: partialOrder?.company_id,
                delivery_date_id: partialOrder?.delivery_date_id,
                owner_id: partialOrder?.owner_id,
                lead_from: partialOrder?.lead_from,
                notification_id: partialOrder?.notification_id,
                payment_term_condition: partialOrder?.payment_term_condition,
                quotation_type_id: partialOrder?.quotation_type_id,
                prepard_by: partialOrder?.prepard_by,
                enq_ref: partialOrder?.enq_ref,
                currency_id: partialOrder?.currency_id,
                product_description: partialOrder?.product_description,
                courier_id: partialOrder?.courier_id,
                id: partialOrderId,
                partial_order_id: partialOrderId,
                customer_id: company_id_info,
                date: partialOrder?.date,
              }));
            },
          );

          setList(flattenedList);
          setCurrentPage(res.current_page || page);
          setPerPage(res.per_page || perPage);
          setTotalItems(res.total || 0);
        } else {
          setList([]);
        }
      } catch (error) {
        console.error("Failed to fetch partial orders:", error);
        setList([]);
      } finally {
        setLoading(false);
        setLoadingSearch(false);
      }
    },
    [
      backendRoute.getPartialOrder,
      searchText,
      startDate,
      endDate,
      ownerFilter,
      branchFilter,
      principalFilter,
      currencyFilter,
    ],
  );

  /* ---------- Export (polling) ---------- */
  const triggerDownload = (url: string, filename: string, revoke = false) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloading(true);
    setTimeout(() => setDownloading(false), 3000);
    setExporting(false);
  };

  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, true);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  const exportFile = useCallback(async () => {
    try {
      setExporting(true);
      setDownloading(false);
      const result = await apiRequestCall(
        "post",
        backendRoute.getPartialOrder,
        {
          data: {
            search: searchText,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            owner_list: ownerFilter,
            branch_list: branchFilter,
            principal_list: principalFilter,
            currency_list: currencyFilter,
            download: true,
          },
        },
      );

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;

      if (!filename) {
        setExporting(false);
        alert("Export request failed. Please try again.");
        return;
      }

      pollForFile(filename);
    } catch (error) {
      console.error("Export request failed:", error);
      setExporting(false);
      setDownloading(false);
      alert("Export failed. Please try again.");
    }
  }, [
    backendRoute.getPartialOrder,
    searchText,
    startDate,
    endDate,
    ownerFilter,
    branchFilter,
    principalFilter,
    currencyFilter,
    pollForFile,
  ]);

  /* ---------- Dropdown fetchers ---------- */
  const fetchDropdownData = useCallback(
    async (
      endpoint: string,
      setter: React.Dispatch<React.SetStateAction<any[]>>,
    ) => {
      try {
        const result = await apiRequestCall(
          "post",
          (backendRoute as any)[endpoint],
        );
        const raw = result?.data?.data;
        if (raw && typeof raw === "object") {
          setter(
            Object.entries(raw).map(([id, name]) => ({
              id: +id,
              name: String(name),
            })),
          );
        } else setter([]);
      } catch (error) {
        console.error(`Failed to fetch ${endpoint}:`, error);
        setter([]);
      }
    },
    [backendRoute],
  );

  const getCompanyList = useCallback(async () => {
    try {
      const result = await apiRequestCall("post", backendRoute.getCompanyDD);
      const raw = result?.data?.data;
      if (Array.isArray(raw)) {
        setCompanyList(
          raw.map((item: any) => ({
            id: item.id,
            company_name: item.company_name || "",
            owner_name: item?.owner?.name || "",
            address: item.address || "",
            state_id: item.state_id,
            other_state: item?.other_state,
            city: item?.city,
            email_id: item?.email_id,
            pin_code: item?.pin_code,
            mobile_no: item?.mobile_no,
            landline_no: item?.landline_no,
            customer_name: item?.customer_name,
          })),
        );
      } else setCompanyList([]);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
      setCompanyList([]);
    }
  }, [backendRoute.getCompanyDD]);

  const getCurrency = useCallback(async () => {
    await fetchDropdownData("getCurrencyDD", setCurrencyList);
  }, [fetchDropdownData]);

  const getOwnerList = useCallback(async () => {
    await fetchDropdownData("getOwnerDD", setOwnerList);
  }, [fetchDropdownData]);

  const getBranch = useCallback(async () => {
    await fetchDropdownData("getBranchDD", setBranchList);
  }, [fetchDropdownData]);

  const getPrincipalList = useCallback(async () => {
    await fetchDropdownData("getPrincipalDD", setPaymentAdvanceDate);
  }, [fetchDropdownData]);

  const getStateList = useCallback(async () => {
    await fetchDropdownData("getStateDD", setStateList);
  }, [fetchDropdownData]);

  const getQuotationTypeList = useCallback(async () => {
    await fetchDropdownData("getQuotationTypeDD", setQuotationTypeList);
  }, [fetchDropdownData]);

  const getNotificationList = useCallback(async () => {
    await fetchDropdownData("getNotificationDD", setNotificationList);
  }, [fetchDropdownData]);

  const getCourierList = useCallback(async () => {
    await fetchDropdownData("getCourierDD", setCourierList);
  }, [fetchDropdownData]);

  /* ---------- Row/product helpers ---------- */
  const deleteProduct = useCallback((index: number) => {
    setProductRows((prev) => prev.filter((_, i) => i !== index));
  }, []);
  const updateRow = useCallback(
    (index: number, field: string, value: any) => {
      if (field !== "send_qty") return;

      const raw = typeof value === "string" ? value.trim() : value;

      // Handle empty input directly
      if (raw === "") {
        setProductRows((prev) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], send_qty: "" };
          return updated;
        });

        setFormError((prev) => {
          if (!prev?.product_list) return prev;
          const next = { ...prev };
          const product_list = { ...next.product_list };
          const rowErrors = { ...(product_list[index] ?? {}) };

          delete rowErrors.send_qty;
          if (Object.keys(rowErrors).length === 0) {
            delete product_list[index];
          } else {
            product_list[index] = rowErrors;
          }

          next.product_list =
            Object.keys(product_list).length > 0 ? product_list : undefined;
          return next;
        });
        return;
      }

      const parsed = Number(raw);
      const safeValue = Number.isFinite(parsed) ? Math.floor(parsed) : 0;

      const balance = Number(productRows?.[index]?.balance_quantity ?? 0);
      const balanceInt = Number.isFinite(balance) ? Math.floor(balance) : 0;

      // If send_qty exceeds balance_qty
      if (safeValue > balanceInt) {
        setProductRows((prev) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], send_qty: "" };
          return updated;
        });

        setFormError((prev) => {
          const next = { ...prev };
          const product_list = { ...(next.product_list ?? {}) };

          product_list[index] = {
            ...(product_list[index] ?? {}),
            send_qty: `Send Qty cannot exceed Balance Qty (${balanceInt})`,
          };

          next.product_list = product_list;
          return next;
        });
        return;
      }

      // Valid case — update and clear any existing error
      setProductRows((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], send_qty: safeValue };
        return updated;
      });

      setFormError((prev) => {
        if (!prev?.product_list) return prev;
        const next = { ...prev };
        const product_list = { ...next.product_list };
        const rowErrors = { ...(product_list[index] ?? {}) };

        delete rowErrors.send_qty;
        if (Object.keys(rowErrors).length === 0) {
          delete product_list[index];
        } else {
          product_list[index] = rowErrors;
        }

        next.product_list =
          Object.keys(product_list).length > 0 ? product_list : undefined;
        return next;
      });
    },
    [productRows],
  );

  /* ---------- Update / delete / submit flows ---------- */
  const updateRecord = useCallback(
    (row: any) => {
      if (row?.order) {
        setOrderTitle(true);
        const selectedCurrency = currencyList.find(
          (c) => c.id === row.currency_id,
        );
        setCurrencyName(selectedCurrency?.name || "");
      } else {
        setOrderTitle(false);
      }
      setFormError({});
      setSelectedId(row.id);
      setShowForm(true);
      setFormData({
        ...row,
        contact_person: row?.billing_contact_person,
        id: row?.order_id,
      });

      const clearedList = (row?.product_list || []).map((item: any) => {
        return {
          ...item,
          send_qty: "",
        };
      });

      setProductRows(clearedList);
    },
    [currencyList],
  );

  const deleteRecord = useCallback((id: any) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteId) return;
    const result = await apiRequestCall("post", backendRoute.deleteInvoice, {
      data: { id: deleteId },
    });
    if (result?.data?.code === 200) {
      fetchList({ page: currentPage, perPage, search: searchText });
      setShowMessage(result?.data?.message);
      setShowSuccess(true);
    }
    setDeleteId(null);
    setShowDeleteModal(false);
  }, [backendRoute.deleteQuotation, deleteId, currentPage, perPage]);

  const resetForm = useCallback(() => {
    setFormData({
      id: null,
      product_specification: DEFAULT_PAYMENT_TERMS,
      payment_term_condition: DEFAULT_PAYMENT_TERMS,
      product_id: "",
      principal_id: "",
      product_description: "",
      principal_type: "",
      total_amount: 0,
      date: "",
      delivery_date_id: "",
      quotation_type_id: null,
      group_id: null,
      company_id: null,
      owner_id: null,
      billing_state_id: null,
      shipping_state_id: null,
      enq_ref: "",
      contact_person: "",
      prepard_by: "",
      currency_id: null,
      delevery_date_id: null,
      payment_term_id: null,
      billing_address: "",
      billing_state: "",
      billing_city: "",
      billing_pin_code: "",
      billing_mobile: "",
      billing_email: "",
      billing_landline: "",
      shipping_address: "",
      shipping_state: "",
      shipping_city: "",
      shipping_pin_code: "",
      shipping_mobile: "",
      shipping_email: "",
      shipping_landline: "",
      overdue_no: "",
      overdues_value: "",
      courier_id: "",
      customer_order_no: "",
      unique_order_no: "",
    });
    setFormError({});
    setSelectedId(null);
    setShowForm(false);
    setProductRows([]);
    setOrderTitle(false);
  }, []);

  const parseErrors = useCallback(
    (errors: Record<string, string>): FormError => {
      const result: FormError = {};
      for (const key in errors) {
        if (key.startsWith("product_list.")) {
          const parts = key.split(".");
          const index = Number(parts[1]);
          const field = parts[2];
          if (!isNaN(index) && field) {
            result.product_list ??= {};
            result.product_list[index] ??= {};
            const readableField = field.replace(/_/g, " ");
            result.product_list[index][field] = `${
              readableField.charAt(0).toUpperCase() + readableField.slice(1)
            } field is required.`;
          }
        } else {
          result[key] = errors[key];
        }
      }
      return result;
    },
    [],
  );

  /* ---------- Invoice upload helpers ---------- */
  const handleRow = useCallback((row: any) => {
    setInvoiceFormError({});
    setInvoiceDate({
      partial_order_id: row?.partial_order_id ?? null,
      invoice_no: "",
      customer_id: row?.customer_id ?? null,
      docket_no: "",
      customer_order_no: row?.customer_order_no ?? null,
      current_follow_date: dayjs().format("YYYY-MM-DD"),
    });
    setProductInvoiceRows([{ invoice: null }]);
    setShowUpload(true);
  }, []);

  const updateInvoice = (
    index: number,
    field: keyof ProductInvoiceRow,
    value: any,
  ) => {
    setInvoiceFormError((prev) => {
      const clone = { ...prev };
      delete clone.product_invoice_list;
      return clone;
    });

    const rows = [...productInvoiceRows];
    rows[index][field] = value;
    setProductInvoiceRows(rows);
  };

  const addInvoice = () =>
    setProductInvoiceRows([...productInvoiceRows, { invoice: null }]);
  const removeInvoice = (index: number) =>
    setProductInvoiceRows(productInvoiceRows.filter((_, i) => i !== index));

  const submitInvoice = async () => {
    const fd = new FormData();
    fd.append("current_follow_date", invoiceDate.current_follow_date);
    fd.append("invoice_no", invoiceDate.invoice_no);
    fd.append("docket_no", invoiceDate.docket_no);
    fd.append("partial_order_id", invoiceDate.partial_order_id);
    fd.append("customer_order_no", invoiceDate.customer_order_no);
    fd.append("customer_id", invoiceDate.customer_id);

    productInvoiceRows.forEach((row, idx) => {
      if (row.invoice instanceof File) {
        fd.append(`product_invoice_list[${idx}][invoice]`, row.invoice);
      }
    });

    const result = await apiFileRequestCall(
      "post",
      backendRoute.addUpdateInvoice,
      { data: fd },
    );
    if (result?.data?.code === 221) {
      setInvoiceFormError(parseValidationErrors(result?.data?.errors));
      return;
    }
    if (result?.data?.code === 200) {
      setShowUpload(false);
      fetchList({ page: currentPage, perPage });
      setShowMessage(result?.data?.message);
      setShowSuccess(true);
    }
    setDeleteId(null);
  };

  /* ---------- Effects ---------- */
  useEffect(() => {
    // fetch dropdowns
    getCurrency();
    getCompanyList();
    getOwnerList();
    getQuotationTypeList();
    getNotificationList();
    getStateList();
    getBranch();
    getCourierList();
    getPrincipalList();
  }, []);

  useEffect(() => {
    fetchList({ page: currentPage, perPage, search: searchText });
  }, [
    searchText,
    startDate,
    endDate,
    perPage,
    currentPage,
    ownerFilter,
    branchFilter,
    principalFilter,
    currencyFilter,
  ]);

  useEffect(() => {
    if (!showForm) return;
    if (!styleTagRef.current) {
      const tag = document.createElement("style");
      tag.innerHTML = `.offcanvas-end.offcanvas-large{width:1500px!important;}`;
      document.head.appendChild(tag);
      styleTagRef.current = tag;
    }
    return () => {
      if (styleTagRef.current) {
        styleTagRef.current.remove();
        styleTagRef.current = null;
      }
    };
  }, [showForm]);

  useEffect(() => {
    if (!editor) return;

    const field =
      editor.field === "Comments" ? "product_specification" : editor.field;

    setEditorValue(productRows[editor.index]?.[field] || "");
  }, [editor, productRows]);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data,
  ) as Permission | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  /* ---------- Columns ---------- */
  const columns = useMemo(
    () => [
      { key: "company_name", label: "Customer Name" },
      { key: "part_no", label: "Part No." },
      { key: "customer_order_no", label: "Customer Order No" },
      { key: "quantity", label: "Quantity" },
      { key: "rate", label: "Rate" },
      { key: "total_amount", label: "Amount" },
      { key: "balance_quantity", label: "Pending Item" },
      {
        key: "send_qty",
        label: "Send Qty",
      },

      {
        key: "created_at",
        label: "Created Date",
        render: (row: any) =>
          row.created_at
            ? dayjs(row.created_at, "DD-MM-YYYY").format("DD/MM/YYYY")
            : "",
      },

      ...(permissionMap?.partial_order?.edit_partial_order ||
      permissionMap?.partial_order?.upload_partial_order ||
      permissionMap?.partial_order?.delete_partial_order
        ? [
            {
              key: "action",
              label: "Action",
              render: (row: any) => (
                <div className="hstack gap-2 fs-15">
                  {permissionMap?.partial_order?.edit_partial_order &&
                    row?.is_checked &&
                    row.product_list?.some(
                      (p: any) => p.balance_quantity != 0 && p.is_checked == 1,
                    ) && (
                      <button
                        className="btn btn-icon btn-sm btn-soft-success"
                        onClick={() => updateRecord({ ...row, order: true })}
                      >
                        <i className="feather-edit" />
                      </button>
                    )}

                  {permissionMap?.partial_order?.upload_partial_order &&
                    row.invoice_id === null &&
                    row?.balance_quantity == 0 && (
                      <button
                        className="btn btn-icon btn-sm btn-soft-warning"
                        onClick={() => handleRow(row)}
                      >
                        <i className="feather-upload" />
                      </button>
                    )}

                  {permissionMap?.partial_order?.delete_partial_order &&
                    row.invoice_id !== null && (
                      <button
                        className="btn btn-icon btn-sm btn-soft-danger"
                        onClick={() => deleteRecord(row.invoice_id)}
                      >
                        <i className="feather-trash" />
                      </button>
                    )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [currentPage, perPage, updateRecord, handleRow, deleteRecord],
  );

  const selectedOption = (opts: { value: any; label: string }[], id?: any) =>
    !id ? null : opts.find((o) => o.value === id) || null;

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        product_list: productRows,
        update_status: !!selectedId,
        quotation_id: formData["id"],
      };

      const route = orderTitle
        ? backendRoute.storePartialOrder
        : backendRoute.addUpdateQuotation;
      const result = await apiRequestCall("post", route, { data: payload });

      if (result?.data?.code === 221) {
        setFormError(parseErrors(result?.data?.errors));
        return;
      }

      if (result?.data?.code === 200) {
        setShowUpload(false);
        resetForm();
        setShowSuccess(true);
        setShowMessage(result?.data?.message);
        fetchList({ page: currentPage, perPage, search: searchText });
      } else {
        setShowMessage(result?.data?.message || "Operation failed");
      }
    } catch (err) {
      console.error("Failed to submit:", err);
      setShowMessage("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [
    backendRoute.addUpdateQuotation,
    backendRoute.storePartialOrder,
    apiRequestCall,
    currentPage,
    fetchList,
    formData,
    orderTitle,
    parseErrors,
    perPage,
    productRows,
    resetForm,
    selectedId,
    setFormError,
    setShowMessage,
    setShowSuccess,
    setSubmitting,
  ]);

  const disabledDate = (current: dayjs.Dayjs | null) =>
    !!current && current.isAfter(dayjs(), "day");

  const handleSelectChange = useCallback((name: string, selected: any) => {
    setFormError((prev) => {
      const u = { ...prev };
      delete (u as any)[name];
      return u;
    });

    if (name === "currency_id") setCurrencyName(selected?.label || "");

    if (name === "company_id") {
      const updates: any = {
        billing_address: selected?.address || null,
        billing_city: selected?.city || null,
        billing_state_id: selected?.state_id || "",
        billing_mobile: selected?.mobile_no || "",
        billing_email: selected?.email_id || "",
        billing_landline: selected?.landline_no || "",
        billing_pin_code: selected?.pin_code || "",
        contact_person: selected?.customer_name || "",
        shipping_address: selected?.address || null,
        shipping_city: selected?.city || null,
        shipping_state_id: selected?.state_id || "",
      };
      if (selected?.other_state) {
        updates.billing_other_state = selected?.other_state || "";
        updates.shipping_other_state = selected?.other_state || "";
      }
      setFormError((prev) => {
        const u = { ...prev };
        Object.keys(updates).forEach((k) => delete (u as any)[k]);
        return u;
      });
      setFormData((prev: any) => ({ ...prev, ...updates }));
      setAddressInfo(selected || {});
    }

    setFormData((prev: any) => ({ ...prev, [name]: selected?.value ?? null }));
  }, []);

  const setRef = (name: string) => (el: HTMLDivElement | null) => {
    fieldRefs.current[name] = el;
  };
  const searchTextData = (text: string) => {
    setSearchText(text);
  };

  useEffect(() => {
    const firstErrorKey = Object.keys(formError)[0];

    if (firstErrorKey && fieldRefs.current[firstErrorKey]) {
      const el = fieldRefs.current[firstErrorKey];

      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      setBlinkField(firstErrorKey);

      setTimeout(() => setBlinkField(null), 1000000); // stop blinking
    }
  }, [formError]);

  useEffect(() => {
    if (!editor) return;

    const field =
      editor.field === "Comments" ? "product_specification" : editor.field;

    setEditorValue(productRows[editor.index]?.[field] || "");
  }, [editor, productRows]);

  /* ---------- Auth check ---------- */
  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  /* ---------- Render ---------- */
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title">
                  Partial Orders
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex justify-content-end gap-2">
                  <Export
                    options={[
                      {
                        label: downloading
                          ? "Downloading..."
                          : exporting
                            ? "Generating..."
                            : "Export",
                        iconClass: downloading
                          ? "ti ti-download"
                          : exporting
                            ? "ti ti-loader"
                            : "ti ti-file-type-xls",
                        colorClass:
                          exporting || downloading
                            ? "text-muted"
                            : "text-primary",
                        onClick:
                          exporting || downloading || submitting
                            ? undefined
                            : exportFile,
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-4">
                <div className="card-control position-relative">
                  <div className="icon-form">
                    <Search
                      placeholder="Enter Text ..."
                      value={query}
                      onChange={setQuery}
                      searchText={searchTextData}
                      loading={loadingSearch}
                    />
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>
                  <DateRangePicker
                    initialSettings={{
                      autoUpdateInput: false,
                      locale: { cancelLabel: "Clear" },
                    }}
                    onCallback={(start, end) => {
                      setStartDate(start.format("YYYY-MM-DD"));
                      setEndDate(end.format("YYYY-MM-DD"));
                    }}
                    onCancel={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                  >
                    <input
                      type="text"
                      className="form-control bookingrange"
                      placeholder="Select Date Range Filter"
                      value={
                        startDate && endDate
                          ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                              endDate,
                            ).format("DD-MM-YYYY")}`
                          : ""
                      }
                      readOnly
                      style={{ cursor: "pointer" }}
                      aria-label="Select date range"
                    />
                  </DateRangePicker>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <FilterDropdown
                  label="Branch"
                  endpoint="/getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <Loader />
              ) : (
                <PartialOrderTable
                  data={list.filter((row: any) => Number(row.is_checked) === 1)}
                  columns={columns}
                />
              )}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(p) => {
              setCurrentPage(p);
              fetchList({ page: p, perPage, search: searchText });
            }}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setCurrentPage(1);
              fetchList({ page: 1, perPage: pp, search: searchText });
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
            ]}
          />
        </div>

        {/* Offcanvas Form */}
        <div
          className={`offcanvas offcanvas-end offcanvas-large ${
            showForm ? "show" : ""
          }`}
          tabIndex={-1}
          role="dialog"
        >
          <div className="offcanvas-header border-bottom offcanvas-large-custom">
            <h5 className="fw-semibold">
              {orderTitle ? "Update Partial Order" : "Order"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                setShowForm(false);
                setOrderTitle(false);
                fetchList({ page: 1, perPage: 10, search: searchText });
              }}
            />
          </div>

          <div className="offcanvas-body">
            {/* form — left col */}
            <div className="row">
              <div className="col 3">
                <div className="mb-1">
                  <label className="col-form-label">Company</label>
                  <input
                    type="text"
                    className="form-control"
                    value={
                      selectedOption(companyOptions, formData.company_id)
                        ?.label || ""
                    }
                    title={
                      selectedOption(companyOptions, formData.company_id)
                        ?.label || ""
                    }
                    readOnly
                    style={{
                      background: "#F6F6F6",
                    }}
                  />
                </div>
              </div>

              <div className="col 3">
                <div className="mb-1">
                  <label className="col-form-label">Contact person</label>
                  <input
                    readOnly
                    type="text"
                    name="contact_person"
                    className="form-control"
                    placeholder="Contact Person"
                    value={formData.contact_person || ""}
                    style={{
                      background: "#F6F6F6",
                    }}
                  />
                </div>
              </div>
              <div className="col 3">
                <div className="mb-1">
                  <label className="col-form-label">Order No.</label>
                  <input
                    readOnly
                    placeholder={"Order No"}
                    name="unique_order_no"
                    type="text"
                    className="form-control"
                    value={formData.unique_order_no || ""}
                    onChange={(e) =>
                      setField("unique_order_no", e.target.value)
                    }
                    style={{
                      background: "#F6F6F6",
                    }}
                  />
                  {formError.unique_order_no && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.unique_order_no}
                    </div>
                  )}
                </div>
              </div>

              {/* right col */}
              <div className="col 3">
                <div className="mb-1">
                  <label className="col-form-label">Quotation No.</label>
                  <input
                    readOnly
                    placeholder={"unique_quotation_no"}
                    name="unique_quotation_no"
                    type="text"
                    className="form-control"
                    value={formData.unique_quotation_no || ""}
                    onChange={(e) =>
                      setField("unique_quotation_no", e.target.value)
                    }
                    style={{
                      background: "#F6F6F6",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="row" ref={setRef("prepard_by")}>
              <div className="col 3">
                <div className="mb-1">
                  <label className="col-form-label">
                    Order Prepard By <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="prepard_by"
                    className="form-control"
                    placeholder="Prepare By"
                    value={formData.prepard_by || ""}
                    onChange={(e) => setField("prepard_by", e.target.value)}
                  />
                  {formError.prepard_by && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.prepard_by}
                    </div>
                  )}
                </div>
              </div>

              <div className="col 3" ref={setRef("prepard_by")}>
                <div className="mb-1">
                  <label className="col-form-label">
                    Customer Order Date <span className="text-danger">*</span>
                  </label>
                  <DatePicker
                    value={
                      formData.date
                        ? dayjs(formData.date, "YYYY-MM-DD")
                        : dayjs()
                    }
                    format="DD-MM-YYYY"
                    disabledDate={disabledDate}
                    onChange={(date) =>
                      setField("date", date ? date.format("YYYY-MM-DD") : "")
                    }
                    className="form-control"
                    style={{ width: "100%" }}
                  />
                  {formError?.date && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError?.date}
                    </div>
                  )}
                </div>
              </div>

              <div className="col 3" ref={setRef("customer_order_no")}>
                <div className="mb-1">
                  <label className="col-form-label">
                    Customer Order No. <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"Customer Order No"}
                    name="customer_order_no"
                    type="text"
                    className="form-control"
                    value={formData.customer_order_no || ""}
                    onChange={(e) =>
                      setField("customer_order_no", e.target.value)
                    }
                  />
                  {formError.customer_order_no && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.customer_order_no}
                    </div>
                  )}
                </div>
              </div>

              <div className="col 3" ref={setRef("courier_id")}>
                <div className="mb-1">
                  <label className="col-form-label">
                    Select Courier
                    <span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Select Preferred Courier"
                    name="courier_id"
                    classNamePrefix="react-select"
                    className="tagging"
                    options={courierOptions}
                    value={selectedOption(courierOptions, formData.courier_id)}
                    onChange={(s) => setField("courier_id", s?.value ?? null)}
                    isClearable
                  />
                </div>
              </div>
            </div>

            <div className="row mb-1">
              <div className="col 6">
                <div className="mb-1">
                  <label className="col-form-label">
                    Billing Address <span className="text-danger">*</span>
                  </label>
                  <textarea
                    placeholder={"Billing"}
                    name="billing_address"
                    className="form-control"
                    style={{ height: "100px" }}
                    value={formData.billing_address || ""}
                    onChange={(e) =>
                      setField("billing_address", e.target.value)
                    }
                  />
                  {formError.billing_address && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_address}
                    </div>
                  )}
                </div>

                {addressInfo?.other_state ? (
                  <div className="mb-1">
                    <label className="col-form-label">
                      Other State <span className="text-danger">*</span>
                    </label>
                    <input
                      placeholder={"State"}
                      name="billing_other_state"
                      type="text"
                      className="form-control"
                      value={formData.billing_other_state || ""}
                      onChange={(e) =>
                        setField("billing_other_state", e.target.value)
                      }
                    />
                  </div>
                ) : (
                  <div className="mb-1">
                    <label className="col-form-label">
                      State <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="billing_state_id"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={stateOptions}
                      value={selectedOption(
                        stateOptions,
                        formData.billing_state_id,
                      )}
                      onChange={(s) =>
                        setField("billing_state_id", s?.value ?? null)
                      }
                      isClearable
                    />
                    {formError.billing_state_id && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.billing_state_id}
                      </div>
                    )}
                  </div>
                )}

                <div className="mb-1">
                  <label className="col-form-label">
                    City <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"City"}
                    name="billing_city"
                    type="text"
                    className="form-control"
                    value={formData.billing_city || ""}
                    onChange={(e) => setField("billing_city", e.target.value)}
                  />
                  {formError.billing_city && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_city}
                    </div>
                  )}
                </div>

                <div className="mb-1">
                  <label className="col-form-label">
                    Pin Code <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"Pin Code"}
                    name="billing_pin_code"
                    type="text"
                    className="form-control"
                    value={formData.billing_pin_code || ""}
                    onChange={(e) =>
                      setField("billing_pin_code", e.target.value)
                    }
                  />
                  {formError.billing_pin_code && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_pin_code}
                    </div>
                  )}
                </div>

                <div className="mb-1">
                  <label className="col-form-label">
                    Mobile <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"Mobile"}
                    name="billing_mobile"
                    type="text"
                    className="form-control"
                    value={formData.billing_mobile || ""}
                    onChange={(e) => setField("billing_mobile", e.target.value)}
                  />
                  {formError.billing_mobile && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_mobile}
                    </div>
                  )}
                </div>

                <div className="mb-1">
                  <label className="col-form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"Email"}
                    name="billing_email"
                    type="text"
                    className="form-control"
                    value={formData.billing_email || ""}
                    onChange={(e) => setField("billing_email", e.target.value)}
                  />
                  {formError.billing_email && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_email}
                    </div>
                  )}
                </div>

                <div className="mb-1">
                  <label className="col-form-label">
                    Landline <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"Landline"}
                    name="billing_landline"
                    type="text"
                    className="form-control"
                    value={formData.billing_landline || ""}
                    onChange={(e) =>
                      setField("billing_landline", e.target.value)
                    }
                  />
                  {formError.billing_landline && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_landline}
                    </div>
                  )}
                </div>
              </div>
              <div className="col 6">
                <div className="mb-1">
                  <label className="col-form-label me-1">
                    Shipping Address <span className="text-danger">*</span>
                  </label>
                  <label className="col-form-label">
                    <input
                      type="checkbox"
                      checked={sameAsBilling}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSameAsBilling(checked);
                        if (checked) {
                          setFormData((prev: any) => ({
                            ...prev,
                            shipping_address: prev.billing_address,
                            shipping_state_id: prev.billing_state_id,
                            shipping_other_state: prev.billing_other_state,
                            shipping_city: prev.billing_city,
                            shipping_pin_code: prev.billing_pin_code,
                            shipping_mobile: prev.billing_mobile,
                            shipping_email: prev.billing_email,
                            shipping_landline: prev.billing_landline,
                          }));
                          setFormError((prev: any) => {
                            const u = { ...prev };
                            delete u.shipping_address;
                            delete u.shipping_state_id;
                            delete u.shipping_other_state;
                            delete u.shipping_city;
                            delete u.shipping_pin_code;
                            delete u.shipping_mobile;
                            delete u.shipping_email;
                            delete u.shipping_landline;
                            return u;
                          });
                        } else {
                          setFormData((prev: any) => ({
                            ...prev,
                            shipping_address: "",
                            shipping_state_id: "",
                            shipping_other_state: "",
                            shipping_city: "",
                            shipping_pin_code: "",
                            shipping_mobile: "",
                            shipping_email: "",
                            shipping_landline: "",
                          }));
                        }
                      }}
                    />{" "}
                    Same as Billing <span className="text-danger">*</span>
                  </label>
                  <textarea
                    placeholder={"Shipping"}
                    name="shipping_address"
                    className="form-control"
                    style={{ height: "100px" }}
                    value={formData.shipping_address || ""}
                    onChange={(e) =>
                      setField("shipping_address", e.target.value)
                    }
                  />
                </div>

                {addressInfo?.other_state ? (
                  <div className="mb-1">
                    <label className="col-form-label">
                      Other State <span className="text-danger">*</span>
                    </label>
                    <input
                      placeholder={"State"}
                      name="shipping_other_state"
                      type="text"
                      className="form-control"
                      value={formData.shipping_other_state || ""}
                      onChange={(e) =>
                        setField("shipping_other_state", e.target.value)
                      }
                    />
                  </div>
                ) : (
                  <div className="mb-1">
                    <label className="col-form-label">
                      State <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="shipping_state_id"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={stateOptions}
                      value={selectedOption(
                        stateOptions,
                        formData.shipping_state_id,
                      )}
                      onChange={(s) =>
                        setField("shipping_state_id", s?.value ?? null)
                      }
                      isClearable
                    />
                  </div>
                )}

                <div className="mb-1">
                  <label className="col-form-label">
                    City <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"City"}
                    name="shipping_city"
                    type="text"
                    className="form-control"
                    value={formData.shipping_city || ""}
                    onChange={(e) => setField("shipping_city", e.target.value)}
                  />
                </div>

                <div className="mb-1">
                  <label className="col-form-label">
                    Pin Code <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"Pin Code"}
                    name="shipping_pin_code"
                    type="text"
                    className="form-control"
                    value={formData.shipping_pin_code || ""}
                    onChange={(e) =>
                      setField("shipping_pin_code", e.target.value)
                    }
                  />
                </div>

                <div className="mb-1">
                  <label className="col-form-label">
                    Mobile <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"Mobile"}
                    name="shipping_mobile"
                    type="text"
                    className="form-control"
                    value={formData.shipping_mobile || ""}
                    onChange={(e) =>
                      setField("shipping_mobile", e.target.value)
                    }
                  />
                </div>

                <div className="mb-1">
                  <label className="col-form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"Email"}
                    name="shipping_email"
                    type="text"
                    className="form-control"
                    value={formData.shipping_email || ""}
                    onChange={(e) => setField("shipping_email", e.target.value)}
                  />
                </div>

                <div className="mb-1">
                  <label className="col-form-label">
                    Landline <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder={"Landline"}
                    name="shipping_landline"
                    type="text"
                    className="form-control"
                    value={formData.shipping_landline || ""}
                    onChange={(e) =>
                      setField("shipping_landline", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
            <div className="row mb-1"></div>

            <div
              style={{
                borderRadius: "14px",
                overflow: "auto",
              }}
            >
              <table
                className="table table-view"
                style={{
                  tableLayout: "auto",
                  fontSize: "12px",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                {/* ================= HEADER ================= */}
                <thead>
                  <tr>
                    <th
                      style={{
                        top: 0,
                        zIndex: 1,
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        textAlign: "center",
                        borderBottom: "1px solid #e5e7eb",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Select [✓]
                    </th>
                    {[
                      "Part No.",
                      "Description",
                      ...(quotationType == "GW"
                        ? ["Principal", "UOM", "Specification & Heading"]
                        : []),
                      "HSN",
                      "Qty",
                      "Bal Qty",
                      "Send Qty",
                      "In Stock",
                      `Price [${currencyName}]`,
                      "Disc %",
                      "Net Price",
                      "IGST %",
                      "Total",
                      "Notes",
                      // "Action",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          top: 0,
                          zIndex: 1,
                          fontSize: "11px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: ".04em",
                          textAlign: "center",
                          padding: "10px 8px",
                          borderBottom: "1px solid #e5e7eb",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {!(closeOrderTitle && h === "Action") && h}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* ================= BODY ================= */}
                <tbody>
                  {productRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={15}
                        style={{ padding: "24px", textAlign: "center" }}
                      >
                        <b>Note:</b> No products added. Use search to add
                        products.
                        {formError.product_list && (
                          <div className="text-danger mt-2">
                            At least one product is required
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    productRows.map((row, index) => (
                      <React.Fragment key={index}>
                        <tr key={index}>
                          <td
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                              borderBottom: "1px solid #f1f5f9",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={row.is_checked}
                              disabled={closeOrderTitle}
                              onChange={(e) => {
                                const checked = e.target.checked;

                                setProductRows((prev) => {
                                  const updated = [...prev];
                                  updated[index].is_checked = checked;

                                  return updated;
                                });
                              }}
                            />
                          </td>
                          {(
                            [
                              "part_no",
                              "description",
                              ...(quotationType == "GW"
                                ? ["principal", "uom", "specification"]
                                : []),
                              "hsn_code",
                              "quantity",
                              "balance_quantity",
                              "send_qty",
                              "in_stock",
                              "price",
                              "discount",
                              "net_price",
                              "igst",
                              "total",
                              "notes",
                            ] as const
                          ).map((field) => {
                            const widths: Record<string, string> = {
                              part_no: "240px",
                              description: "20px",
                              principal: "150px",
                              uom: "70px",
                              specification: "20px",
                              hsn_code: "150px",
                              quantity: "120px",
                              balance_quantity: "40px",
                              send_qty: "20px",
                              in_stock: "20px",
                              price: "100px",
                              discount: "40px",
                              net_price: "100px",
                              igst: "50px",
                              total: "100px",
                              notes: "30px",
                            };

                            return (
                              <td
                                key={field}
                                style={{
                                  minWidth: widths[field],
                                  maxWidth: widths[field],
                                  padding: "2px",
                                  verticalAlign: "middle",
                                  borderBottom: "1px solid #f1f5f9",
                                }}
                              >
                                {[
                                  "description",
                                  "specification",
                                  "notes",
                                  "product_description",
                                ].includes(field) ? (
                                  <button
                                    className="btn btn-sm-outline w-100"
                                    style={{
                                      borderRadius: "8px",
                                      border: "1px dashed #cbd5e1",
                                      background: "#f8fafc",
                                    }}
                                    disabled={
                                      formData?.pending_quotation_details
                                        ?.status_code === "win"
                                    }
                                    onClick={() =>
                                      openEditor(index, field as any)
                                    }
                                  >
                                    {row[field] != "" &&
                                    row[field] != undefined ? (
                                      <>
                                        <i className="ti ti-pencil" />
                                      </>
                                    ) : (
                                      <>
                                        <i className="ti ti-plus" />
                                      </>
                                    )}
                                  </button>
                                ) : field === "quantity" ? (
                                  <div className="input-group input-group-sm">
                                    <button
                                      className="btn btn-outline-success"
                                      disabled={true}
                                      onClick={() =>
                                        updateRow(
                                          index,
                                          field,
                                          numberOrZero(row[field]) - 1,
                                        )
                                      }
                                    >
                                      −
                                    </button>

                                    <input
                                      disabled={true}
                                      className="form-control text-center"
                                      value={row[field] ?? 0}
                                      onChange={(e) =>
                                        updateRow(
                                          index,
                                          field,
                                          numberOrZero(e.target.value),
                                        )
                                      }
                                      style={{
                                        fontSize: "12px",
                                        background: ["quantity"].includes(field)
                                          ? "#f8fafc"
                                          : "#ffffff",
                                        borderRadius: "8px",
                                      }}
                                    />
                                    <button
                                      disabled={true}
                                      className="btn btn-outline-success"
                                      onClick={() =>
                                        updateRow(
                                          index,
                                          field,
                                          numberOrZero(row[field]) + 1,
                                        )
                                      }
                                    >
                                      +
                                    </button>
                                  </div>
                                ) : (
                                  <input
                                    type={
                                      field === "send_qty" ? "number" : "text"
                                    }
                                    className="form-control form-control-md"
                                    min={field === "send_qty" ? 0 : undefined}
                                    placeholder={
                                      field === "send_qty" ? "0" : undefined
                                    }
                                    readOnly={
                                      [
                                        "part_no",
                                        "net_price",
                                        "in_stock",
                                        "total",
                                        "uom",
                                        "hsn_code",
                                        "price",
                                        "discount",
                                        "igst",
                                        "balance_quantity",
                                      ].includes(field) || closeOrderTitle
                                    }
                                    value={
                                      field === "send_qty"
                                        ? closeOrderTitle
                                          ? row["quantity"]
                                          : (row[field] ?? "0")
                                        : field === "principal"
                                          ? typeof row[field] === "object"
                                            ? (row[field]?.type ?? "")
                                            : (row[field] ?? "")
                                          : field === "uom"
                                            ? typeof row[field] === "object"
                                              ? (row[field]?.uom ?? "")
                                              : (row[field] ?? "")
                                            : (row[field] ?? "")
                                    }
                                    onChange={(e) =>
                                      updateRow(index, field, e.target.value)
                                    }
                                    style={{
                                      fontSize: "12px",
                                      background:
                                        [
                                          "net_price",
                                          "total",
                                          "part_no",
                                          "in_stock",
                                          "uom",
                                          "hsn_code",
                                          "price",
                                          "discount",
                                          "igst",
                                          "balance_quantity",
                                        ].includes(field) || closeOrderTitle
                                          ? "#f8fafc"
                                          : "#ffffff",
                                      borderRadius: "8px",
                                    }}
                                  />
                                )}
                              </td>
                            );
                          })}

                          {/* {!closeOrderTitle && (
                            <td
                              className="d-flex gap-1"
                              style={{ justifyContent: "center" }}
                            >
                              <button
                                disabled={
                                  formData?.pending_quotation_details
                                    ?.status_code == "win"
                                }
                                type="button"
                                className="btn btn-icon btn-sm btn-soft-secondary"
                                onClick={() =>
                                  openEditor(index, "Comments" as any)
                                }
                                title="Add Comments"
                              >
                                <i className="fa fa-comment" />
                              </button>
                              <button
                                className="btn btn-icon btn-sm btn-soft-danger"
                                disabled={
                                  productRows.length === 1 ||
                                  formData?.pending_quotation_details
                                    ?.status_code === "win"
                                }
                                onClick={() => deleteProduct(index)}
                              >
                                <i className="feather-trash" />
                              </button>
                            </td>
                          )} */}
                        </tr>
                        {/* {row.showDescriptionEditor && (
                            <tr>
                              <td colSpan={13}>
                                <QuillEditor
                                  value={row.product_specification}
                                  onChange={(html: any) => {
                                    setProductRows((prev) => {
                                      const updated = [...prev];
                                      updated[index].product_specification =
                                        html;
                                      return updated;
                                    });
                                  }}
                                  placeholder="Write Comment ..."
                                  height={70}
                                />
                                {formError.product_list?.[index]
                                  ?.product_specification && (
                                  <div className="invalid-feedback d-block text-danger">
                                    {
                                      formError.product_list[index]
                                        .product_specification
                                    }
                                  </div>
                                )}
                              </td>
                            </tr>
                          )} */}
                      </React.Fragment>
                    ))
                  )}
                </tbody>

                {/* ================= FOOTER ================= */}
                {!isMobile && productRows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={8}></td>
                      <td
                        colSpan={quotationType == "GW" ? 5 : 1}
                        style={{
                          textAlign: "right",
                          fontWeight: 600,
                          padding: "12px",
                          borderTop: "2px solid #e5e7eb",
                        }}
                      >
                        Sub Total
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          fontWeight: 700,
                        }}
                      >
                        {subTotal.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <div className="row" style={{ marginTop: "-15px" }}>
              <div className="col-6">
                <div className="mb-0">
                  <label className="col-form-label">Extra Notes</label>
                  <textarea
                    placeholder={"Extra note , if any !"}
                    name="extra_notes"
                    className="form-control"
                    style={{ height: "100px" }}
                    value={formData.extra_notes || ""}
                    onChange={(e) => setField("extra_notes", e.target.value)}
                  />
                </div>
              </div>
              <div className={"col-6"}>
                <div className="mb-1">
                  <label className="col-form-label">
                    Payment Term
                    <span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Select Term"
                    name="delivery_date_id"
                    classNamePrefix="react-select"
                    className="tagging"
                    options={paymentAdvanceOptions}
                    value={selectedOption(
                      paymentAdvanceOptions,
                      formData.delivery_date_id,
                    )}
                    onChange={(s) => handleSelectChange("delivery_date_id", s)}
                    isClearable
                  />
                  {formError.delivery_date_id && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.delivery_date_id}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              className="d-flex justify-content-end"
              style={{ marginTop: "10px" }}
            >
              <button
                type="button"
                className="btn btn-soft-primary me-2"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="btn btn-soft-success"
                disabled={submitting}
                type="button"
              >
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {orderTitle && selectedId
                      ? "Generate Partial Order"
                      : selectedId
                        ? "Updating..."
                        : "Creating..."}
                  </>
                ) : orderTitle && selectedId ? (
                  "Generate Partial Order"
                ) : selectedId ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </button>
            </div>
          </div>
        </div>

        {editor && (
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(0,0,0,.4)" }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editor.field === "product_specification"
                      ? "COMMENTS"
                      : editor.field.replace("_", " ").toUpperCase()}{" "}
                    {`( ROW => ${editor.index + 1} )`}
                  </h5>
                  <button className="btn-close" onClick={closeEditor} />
                </div>

                <div className="modal-body">
                  {editor.field === "notes" ||
                  editor.field === "description" ? (
                    <textarea
                      disabled
                      className="form-control"
                      rows={8}
                      value={editorValue}
                      onChange={(e) => setEditorValue(e.target.value)}
                      style={{
                        background: "#F6F6F6",
                      }}
                    />
                  ) : (
                    <QuillEditor
                      value={editorValue}
                      onChange={(html: string) => setEditorValue(html)}
                      height={200}
                      placeholder="Write ..."
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Invoice Upload Modal */}
        <Upload
          id="messageModal"
          title="Upload Invoice"
          show={showUpload}
          onHide={() => setShowUpload(false)}
          pendingSubmitting={submitting}
          footer={
            <>
              <button
                type="button"
                className="btn btn-soft-danger"
                onClick={() => setShowUpload(false)}
                disabled={submitting}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-soft-success"
                onClick={submitInvoice}
              >
                Submit
              </button>
            </>
          }
        >
          <form>
            <div className="mb-3">
              <label className="form-label">Current Date :</label>
              <DatePicker
                value={dayjs(invoiceDate?.current_follow_date, "YYYY-MM-DD")}
                format="DD-MM-YYYY"
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                onChange={(date) => {
                  const formatted = date ? date.format("YYYY-MM-DD") : "";
                  setInvoiceDate({
                    ...invoiceDate,
                    current_follow_date:
                      formatted || dayjs().format("YYYY-MM-DD"),
                  });
                }}
                className="form-control"
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Invoice No.
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="invoice_no"
                className="form-control"
                placeholder="Invoice No."
                value={invoiceDate.invoice_no}
                onChange={(e) => {
                  const value = e.target.value;

                  setInvoiceDate((prev) => ({
                    ...prev,
                    invoice_no: value,
                  }));

                  setInvoiceFormError((prev) => {
                    const temp = { ...prev };
                    delete temp.invoice_no;
                    return temp;
                  });
                }}
              />
              {invoiceFormError.invoice_no && (
                <div className="invalid-feedback d-block text-danger">
                  {invoiceFormError.invoice_no}
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Docket No
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="docket_no"
                className="form-control"
                placeholder="Docket No"
                value={invoiceDate.docket_no}
                onChange={(e) => {
                  const val = e.target.value;
                  setInvoiceDate((prev) => ({ ...prev, docket_no: val }));
                  setInvoiceFormError((prev) => {
                    const clone = { ...prev };
                    delete clone.docket_no;
                    return clone;
                  });
                }}
              />
              {invoiceFormError.docket_no && (
                <div className="invalid-feedback d-block text-danger">
                  {invoiceFormError.docket_no}
                </div>
              )}
            </div>

            <div className="mb-3 col-12" style={{ fontSize: "12px" }}>
              <div className="table-responsive">
                <table
                  className="table table-view"
                  style={{ fontSize: "12px" }}
                >
                  <thead>
                    <tr>
                      <th>Upload Invoice</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productInvoiceRows.map((row, index) => (
                      <tr key={index}>
                        <td style={{ minWidth: "160px", padding: "4px" }}>
                          <input
                            className="form-control form-control-md"
                            type="file"
                            onChange={(e) =>
                              updateInvoice(
                                index,
                                "invoice",
                                e.target.files?.[0] || null,
                              )
                            }
                          />
                        </td>
                        <td style={{ minWidth: "80px" }}>
                          <div className="d-flex gap-1">
                            {index === productInvoiceRows.length - 1 && (
                              <button
                                type="button"
                                className="btn btn-icon btn-sm btn-soft-success"
                                onClick={addInvoice}
                                title="Add Row"
                              >
                                <i className="feather-plus" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-soft-danger"
                              onClick={() => removeInvoice(index)}
                              disabled={productInvoiceRows.length === 1}
                              title="Delete Row"
                            >
                              <i className="feather-minus" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {invoiceFormError.product_invoice_list && (
                  <div className="invalid-feedback d-block text-danger">
                    {invoiceFormError.product_invoice_list}
                  </div>
                )}
              </div>
            </div>
          </form>
        </Upload>

        <Success
          id="formatSuccessModal"
          title="Partial Order"
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />

        <Delete
          show={showDeleteModal}
          title="Delete Partial Order?"
          message="Are you sure?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default PartialOrder;
